import React, { useState, useRef, useEffect } from 'react';
import { searchLocation } from '../../services/api';
import { SEARCH_CONFIG } from '../../utils/constants';

const POI_SUGGESTIONS = [
  { id: 'restaurants', label: 'Restaurants', matchTerms: ['restaurant', 'restaurants', 'rest'], poiCategoryId: 'food' },
  { id: 'health', label: 'Health', matchTerms: ['health', 'hospital', 'clinic', 'pharmacy'], poiCategoryId: 'health' },
  { id: 'schools', label: 'Schools', matchTerms: ['school', 'schools', 'college', 'university'], poiCategoryId: 'education' },
  { id: 'banks', label: 'Banks', matchTerms: ['bank', 'banks', 'atm'], poiCategoryId: 'finance' },
  { id: 'malls', label: 'Malls', matchTerms: ['mall', 'malls', 'marketplace'], poiCategoryId: 'shopping' },
  { id: 'gas-station', label: 'Gas Station', matchTerms: ['gas', 'fuel', 'station', 'gas station'], poiCategoryId: 'services' },
  { id: 'dlv-facilities', label: 'Dlv Facilities', matchTerms: ['dlv', 'delhivery', 'facility', 'facilities'], poiCategoryId: 'facility' }
];

const getPoiSuggestions = (query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return [];
  }

  return POI_SUGGESTIONS
    .filter((suggestion) => {
      const label = suggestion.label.toLowerCase();
      return (
        label.startsWith(normalizedQuery) ||
        label.includes(` ${normalizedQuery}`) ||
        suggestion.matchTerms.some((term) => term.startsWith(normalizedQuery))
      );
    })
    .map((suggestion) => ({
      id: `poi-suggestion-${suggestion.id}`,
      display_text: suggestion.label,
      type: 'poi_suggestion',
      query: suggestion.label,
      poiCategoryId: suggestion.poiCategoryId
    }));
};

const SearchBar = ({
  onResultSelect,
  onPoiSuggestionSelect,
  isRoutingMode,
  mapRef,
  onToggleRouting,
  onDirectionsFromSearch,
  onSearchPerformed,
  clearSignal
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lastSelectedResult, setLastSelectedResult] = useState(null);
  const searchTimeoutRef = useRef(null);
  const undoHistoryRef = useRef([]);
  const MAX_UNDO_HISTORY = 50;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (clearSignal === undefined) return;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setSelectedIndex(0);
    setSearchLoading(false);
    setLastSelectedResult(null);
    undoHistoryRef.current = [];
  }, [clearSignal]);

  const performSearch = async (query) => {
    if (query.trim().length < SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const poiSuggestions = getPoiSuggestions(query);
      // Get map center for lat/lng
      const center = mapRef.current?.getCenter();
      const lat = center ? parseFloat(center.lat).toFixed(6) : '28.6304';
      const lng = center ? parseFloat(center.lng).toFixed(6) : '77.1277';

      // Use v2 API (main search API)
      const results = await searchLocation(query, lat, lng);

      const normalizedSuggestionLabels = new Set(
        poiSuggestions.map((suggestion) => suggestion.display_text.toLowerCase())
      );
      const filteredResults = results.filter(
        (result) => !normalizedSuggestionLabels.has((result.display_text || '').toLowerCase())
      );
      const mergedResults = [...poiSuggestions, ...filteredResults];
      setSearchResults(mergedResults);
      setShowDropdown(true); // Show dropdown even when no results (to display "no results" message)
      
      // Track search for feedback
      if (onSearchPerformed) {
        onSearchPerformed(query, filteredResults);
      }
    } catch (error) {
      // Search error
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    if (query !== searchQuery) {
      if (searchQuery.length > 0) {
        undoHistoryRef.current.push(searchQuery);
        if (undoHistoryRef.current.length > MAX_UNDO_HISTORY) {
          undoHistoryRef.current.shift();
        }
      }
    }
    setSearchQuery(query);
    setSelectedIndex(0); // Reset selection when typing
    setLastSelectedResult(null); // Clear selected result when typing new query

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length >= SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, SEARCH_CONFIG.DEBOUNCE_DELAY);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
      e.preventDefault();
      if (undoHistoryRef.current.length > 0) {
        const prev = undoHistoryRef.current.pop();
        setSearchQuery(prev);
        setSelectedIndex(0);
        if (prev.trim().length >= SEARCH_CONFIG.MIN_QUERY_LENGTH) {
          performSearch(prev);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      }
      return;
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(0);
    } else if (e.key === 'ArrowDown' && searchResults.length > 0) {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % searchResults.length);
    } else if (e.key === 'ArrowUp' && searchResults.length > 0) {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      handleResultSelect(searchResults[selectedIndex]); // Select highlighted result
    }
  };

  const handleResultSelect = (result) => {
    if (result.type === 'poi_suggestion') {
      const nextQuery = result.query || result.display_text || '';
      setSearchQuery(nextQuery);
      setShowDropdown(false);
      setSearchResults([]);
      setSelectedIndex(0);
      setLastSelectedResult(null);
      if (onPoiSuggestionSelect) {
        onPoiSuggestionSelect(result);
      }
      return;
    }

    setSearchQuery(result.display_text || '');
    setShowDropdown(false);
    setLastSelectedResult(result);
    onResultSelect(result);
  };

  return (
    <div className="search-container">
      <div className="search-bar-wrapper">
        <div className="search-icon-left">
          <img
            src="/icons/searchBarSearchIcon.png"
            alt="Search"
            width="24"
            height="24"
            className="search-icon"
          />
        </div>
        <input
          type="text"
          className="search-input"
          placeholder={isRoutingMode && !document.body.classList.contains('mweb') ? "Search disabled in routing mode" : "Search Delhivery Maps"}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => {
            if (searchResults.length > 0 || (searchQuery.trim().length >= SEARCH_CONFIG.MIN_QUERY_LENGTH && !searchLoading)) {
              setShowDropdown(true);
            }
          }}
          disabled={isRoutingMode && !document.body.classList.contains('mweb')}
          autoFocus
        />
        <div className="search-icons-container">
          {/* Routing Button */}
          <button
            className="search-routing-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(false);

              // Priority 1: Use the previously selected/clicked result
              if (lastSelectedResult && onDirectionsFromSearch) {
                onDirectionsFromSearch(lastSelectedResult);
                return;
              }

              // Priority 2: Use the first valid result from the dropdown
              const firstValidResult = searchResults.find((result) => result.type !== 'poi_suggestion');
              if (firstValidResult && onDirectionsFromSearch) {
                onDirectionsFromSearch(firstValidResult);
                return;
              }

              // Priority 3: Just toggle routing mode
              if (onToggleRouting) onToggleRouting();
            }}
            title="Get Directions"
          >
            <img
              src="/icons/_v1rightDirectionIcon.png"
              alt="Get Directions"
              width="24"
              height="24"
            />
            {searchLoading && (
              <div className="search-routing-loader-overlay">
                <div className="spinner-small"></div>
              </div>
            )}
          </button>
        </div>

        {/* Dropdown Results */}
        {showDropdown && (searchResults.length > 0 || (searchQuery.trim().length >= SEARCH_CONFIG.MIN_QUERY_LENGTH && !searchLoading)) && (
          <div className="search-dropdown">
            <div className="search-dropdown-content">
              {searchResults.length > 0 ? searchResults.map((result, idx) => (
                <div
                  key={result.id || idx}
                  className={`search-result-item ${idx === selectedIndex ? 'keyboard-selected' : ''}`}
                  onClick={() => handleResultSelect(result)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <div className="search-result-icon-container">
                    <img
                      src={result.type === 'poi_suggestion'
                        ? "/icons/suggestedSearchPoi.png"
                        : "/pin/_v1searchMarkerPin.png"}
                      alt={result.type === 'poi_suggestion' ? "poi suggestion" : "location"}
                      className={`search-result-icon${result.type === 'poi_suggestion' ? ' poi-suggestion-icon' : ''}`}
                    />
                  </div>
                  <div className="search-result-content">
                    <div className="search-result-heading">
                      {result.display_text}
                    </div>
                    {result.distance_km !== undefined && result.distance_km !== null && (
                      <div className="search-result-distance">
                        {result.distance_km === 0
                          ? 'At this location'
                          : `${Number(result.distance_km).toFixed(2)} km away`}
                      </div>
                    )}
                  </div>
                </div>
              )) : (
                <div className="search-result-item search-no-results">
                  <span>Couldn't find any result with "{searchQuery.trim()}"</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;